import {
  UsersIcon,
  UserIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CheckCircleIcon,
  NewspaperIcon,
  QuestionMarkCircleIcon,
  StarIcon,
  ClockIcon
} from '@heroicons/react/24/outline'

type IconType = 'users' | 'user-check' | 'academic-cap' | 'document-text' | 'chart-bar' | 'check-circle' | 'newspaper' | 'question-mark-circle' | 'star' | 'clock'
type ColorType = 'blue' | 'green' | 'purple' | 'yellow' | 'indigo' | 'red'

interface StatsCardProps {
  title: string
  value: number | string
  icon: IconType
  color: ColorType
  subtitle?: string
  trend?: {
    value: number
    isPositive: boolean
  }
}

const iconMap = {
  'users': UsersIcon,
  'user-check': UserIcon,
  'academic-cap': AcademicCapIcon,
  'document-text': DocumentTextIcon,
  'chart-bar': ChartBarIcon,
  'check-circle': CheckCircleIcon,
  'newspaper': NewspaperIcon,
  'question-mark-circle': QuestionMarkCircleIcon,
  'star': StarIcon,
  'clock': ClockIcon
}

const colorMap = {
  blue: {
    bg: 'bg-primary-50',
    icon: 'text-primary-600',
    text: 'text-primary-900'
  },
  green: {
    bg: 'bg-success-50',
    icon: 'text-success-600',
    text: 'text-success-900'
  },
  purple: {
    bg: 'bg-primary-100',
    icon: 'text-primary-700',
    text: 'text-primary-800'
  },
  yellow: {
    bg: 'bg-secondary-50',
    icon: 'text-secondary-600',
    text: 'text-secondary-900'
  },
  indigo: {
    bg: 'bg-primary-200',
    icon: 'text-primary-800',
    text: 'text-primary-900'
  },
  red: {
    bg: 'bg-red-50',
    icon: 'text-red-600',
    text: 'text-red-900'
  }
}

const StatsCard = ({ title, value, icon, color, subtitle, trend }: StatsCardProps) => {
  const IconComponent = iconMap[icon]
  const colors = colorMap[color]

  const displayValue = typeof value === 'number' ? value.toLocaleString('pt-BR') : value

  return (
    <div className="group relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow-bizu border border-gray-100 hover:shadow-bizu-lg hover:border-primary-200 transition-all duration-300 ease-in-out hover:-translate-y-1 sm:px-6 sm:pt-6">
      <dt>
        <div className={`absolute rounded-md p-3 ${colors.bg} group-hover:scale-110 transition-transform duration-300`}>
          <IconComponent className={`h-6 w-6 ${colors.icon}`} aria-hidden="true" />
        </div>
        <p className="ml-16 truncate text-sm font-medium text-accent-600">{title}</p>
      </dt>
      <dd className="ml-16 flex flex-col pb-6 sm:pb-7">
        <div className="flex items-baseline">
          <p className={`text-2xl font-semibold ${colors.text} transition-colors duration-300`}>
            {displayValue}
          </p>
          {trend && (
            <p className={`ml-2 flex items-baseline text-sm font-semibold ${trend.isPositive ? 'text-green-600' : 'text-red-600'
              }`}>
              {trend.isPositive ? '+' : ''}{trend.value}%
            </p>
          )}
        </div>
        {subtitle && (
          <p className="mt-1 text-xs text-gray-500">{subtitle}</p>
        )}
      </dd>

      {/* Decorative gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent to-primary-50 opacity-0 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none"></div>
    </div>
  )
}

export default StatsCard